import { z } from 'zod';

export enum TicketStatus {
  NEW = 'NEW',
  OPEN = 'OPEN',
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  RESOLVED = 'RESOLVED',
  CLOSED = 'CLOSED',
  CANCELLED = 'CANCELLED',
}

export enum TicketPriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  URGENT = 'URGENT',
  CRITICAL = 'CRITICAL',
}

export enum ResolutionType {
  FULLY_CLOSED = 'FULLY_CLOSED',
  WORKAROUND = 'WORKAROUND',
  PARTIALLY_CLOSED = 'PARTIALLY_CLOSED',
}

export enum TicketSourceType {
  VULNERABILITY = 'vulnerability',
  INCIDENT = 'incident',
  MANUAL = 'manual',
}

export enum TicketProviderType {
  LOCAL = 'local',
  TRELLO = 'trello',
  JIRA = 'jira',
  CLICKUP = 'clickup',
}

/**
 * Provider ids are free-form so ticketing systems can be added as plugins.
 * `TicketProviderType` lists the ids shipped with the platform.
 */
// eslint-disable-next-line sonarjs/redundant-type-aliases -- named alias documents intent across plugin packages
export type TicketProviderId = string;

export const ticketProviderIdSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, 'Ticket provider id is required');

export const ticketStatusSchema = z.enum(TicketStatus);
export const ticketPrioritySchema = z.enum(TicketPriority);
export const resolutionTypeSchema = z.enum(ResolutionType);
export const ticketProviderTypeSchema = z.enum(TicketProviderType);
export const ticketProviderTypeInputSchema = z.preprocess((value) => {
  if (typeof value !== 'string') {
    return value;
  }

  return value.trim().toLowerCase();
}, ticketProviderTypeSchema);
export const syncTicketProviderTypeSchema = z.enum([
  TicketProviderType.TRELLO,
  TicketProviderType.JIRA,
  TicketProviderType.CLICKUP,
] as const);

export function parseTicketStatus(value: string | undefined): TicketStatus | undefined {
  return ticketStatusSchema.safeParse(value).data;
}

export function parseTicketPriority(
  value: string | undefined,
): TicketPriority | undefined {
  return ticketPrioritySchema.safeParse(value).data;
}

export function parseResolutionType(
  value: string | undefined,
): ResolutionType | undefined {
  return resolutionTypeSchema.safeParse(value).data;
}

export function parseTicketProviderType(
  value: string | undefined,
): TicketProviderType | undefined {
  return ticketProviderTypeInputSchema.safeParse(value).data;
}

export function parseTicketProviderId(
  value: string | null | undefined,
): TicketProviderId | undefined {
  return ticketProviderIdSchema.safeParse(value).data;
}

export function isResolvedTicketStatus(status: TicketStatus): boolean {
  return status === TicketStatus.RESOLVED || status === TicketStatus.CLOSED;
}

// ─── Provider Client Interfaces ──────────────────────────────────────────────

export interface CreateTicketPayload {
  title: string;
  description: string;
  priority: TicketPriority;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface TicketResponse {
  externalId: string;
  externalNumber: string;
  url?: string;
  status: string;
  rawResponse?: unknown;
}

export interface TicketProviderConfig {
  provider: TicketProviderId;
  enabled: boolean;
  credentials: Record<string, string>;
  settings: Record<string, unknown>;
}

// ─── Status Reader Interfaces ────────────────────────────────────────────────

export interface TicketStatusInfo {
  externalId: string;
  status: TicketStatus | null;
  statusKnown: boolean;
  rawStatus: string;
  title?: string;
  resolvedAt?: Date;
  /** iTop resolution fields. Other providers leave these unset. */
  resolutionCode?: string;
  solution?: string;
  resolveSeconds?: number;
  lastUpdated?: Date;
  metadata?: Record<string, unknown>;
}

export interface BulkStatusResult {
  successful: TicketStatusInfo[];
  failed: Array<{ externalId: string; error: string }>;
}

export interface RateLimitInfo {
  requestsPerMinute: number;
  batchSize: number;
}
